import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { CollectionNode, ContactDisplay, Node } from "../types";
import { TextField, Autocomplete, IconButton, Tooltip, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import { getMarqueEditorialWithCollections } from "../action/marqueEditorialAction";
import { getContact } from "../action/contactAction";
import { notifyError } from "../action/notification.actions";
import { useAppDispatch } from "../store";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface Props {
    contacts: ContactDisplay[],
    deleteContactRow: Function,
    addContactRow: Function
}

export const ContactTable = (props: Props) => {
    const { contacts, deleteContactRow, addContactRow } = props;
    const marqueEditorialList = useSelector(({ marqueEditorial }: State) => marqueEditorial.collections);

    const [marqueEditorial, setMarqueEditorial] = useState<Node>();
    const [collection, setCollection] = useState<CollectionNode>();
    
    const [data, setData] = useState<ContactDisplay[]>(contacts);

    useEffect(() => {
        setData(contacts);
    }, [contacts]);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getMarqueEditorialWithCollections());
        dispatch(getContact());
    }, [dispatch]);

    const validateEmail = (email: string) => {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    const addContact = (newContact: ContactDisplay) => {
        if (newContact.marque === undefined) {
            dispatch(notifyError("La marque est obligatoire."));
            return;
        }
        if (newContact.email === undefined || !validateEmail(newContact.email)) {
            dispatch(notifyError("L'email n'est pas valide."));
            return;
        }
        
        // Appel de la prop parent
        addContactRow(newContact, newContact.marque, newContact.collection);
        
        // On crée un NOUVEAU tableau en copiant l'ancien (...data) et en ajoutant le nouveau
        setData([...data, newContact]); 
    };

    const deleteContact = (oldContact: ContactDisplay) => {
        deleteContactRow(oldContact);
        // On filtre pour créer un nouveau tableau sans le contact supprimé
        setData(data.filter((contact) => contact !== oldContact));
    };

    const columns = useMemo<MRT_ColumnDef<ContactDisplay>[]>(() => [
        { header: 'Nom', accessorKey: 'firstname' },
        { header: 'Prénom', accessorKey: 'lastname' },
        { header: 'E-mail', accessorKey: 'email' },
        {
            header: 'Marque', accessorKey: 'marque',
            Cell: ({ row }) => <label>{row.original.marque?.label}</label>,
            Edit: ({ row }) => (
                <Autocomplete
                    options={marqueEditorialList}
                    getOptionLabel={(option) => option?.label || ''}
                    value={(marqueEditorial !== undefined ? marqueEditorial : row.original.marque) || null}
                    onChange={(event, newValue) => {
                        setMarqueEditorial(newValue as any);
                    }}
                    renderInput={(params) => <TextField {...params} label="Marque" variant="outlined" />}
                />
            )
        },
        {
            header: 'Collection', accessorKey: 'collection',
            Cell: ({ row }) => <label>{row.original.collection?.label || ''}</label>,
            Edit: ({ row }) => (
                <Autocomplete
                    options={(marqueEditorial !== undefined ? marqueEditorial : row.original.marque)?.children || []}
                    getOptionLabel={(option) => option?.label || ''}
                    value={(collection !== undefined ? collection : row.original.collection) || null}
                    onChange={(event, newValue) => {
                        setCollection(newValue as any);
                    }}
                    renderInput={(params) => <TextField {...params} label="Collection" variant="outlined" />}
                />
            )
        }
    ], [collection, marqueEditorial, marqueEditorialList]);

    const table = useMaterialReactTable({
        columns,
        data: data || [],
        
        // On active l'édition UNIQUEMENT pour permettre la création (Ajout)
        enableEditing: true, 
        createDisplayMode: 'row', 
        editDisplayMode: 'row',
        positionActionsColumn: 'last',        
        localization: MRT_Localization_FR,
        enableStickyHeader: true,
        enableFullScreenToggle: false, // Enlève le bouton plein écran
        enableDensityToggle: false,    // Enlève le bouton de densité (compact/large)
        enableHiding: false,           // Enlève le bouton pour masquer les colonnes
        enableColumnActions: false,
        defaultColumn: {
            minSize: 20,
            size: 50,
        },
        
        onCreatingRowSave: async ({ values, table }) => {
            const newContact = {
                ...values,
                marque: marqueEditorial,
                collection: collection
            } as ContactDisplay; // Cast nécessaire pour rassurer TS

            addContact(newContact);
            
            table.setCreatingRow(null);
            setMarqueEditorial(undefined);
            setCollection(undefined);
        },

        // On affiche uniquement le bouton supprimer
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                <Tooltip title="Effacer">
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                            if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
                                deleteContact(row.original);
                            }
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        
        renderTopToolbarCustomActions: ({ table }) => (
            <Tooltip title="Ajouter un membre" >
            <IconButton
                color="primary"
                onClick={() => {
                    setMarqueEditorial(undefined);
                    setCollection(undefined);
                    table.setCreatingRow(true);
                }}
            >
                <AddIcon />
            </IconButton>
            
            </Tooltip>
        ),
        muiTopToolbarProps: {
        sx: {
            '& > div': {
                justifyContent: 'flex-end',
                gap: '1rem',
            },
        },
    },
    });

    return (
        <div className="members-table">
            <MaterialReactTable table={table} />
        </div>
    );
}